import React from "react";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Section from "../ui/Section";
import { CONTACT, whatsappUrl } from "../../lib/email";

const ContactPanel = () => {
  // Optimized WhatsApp pre-filled message
  const whatsappMessage = `Hello GlowLAWNS Team,
I would like to check availability and packages for Moonrise Valley.
Please assist me with details.`;

  return (
    <Section className="py-16 md:py-20">
      <div 
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl"
        data-testid="contact-panel"
      >
        {/* Ambient glow */}
        <div className="absolute inset-0 opacity-70 pointer-events-none [background:radial-gradient(circle_at_20%_20%,rgba(201,162,39,0.2),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(16,185,129,0.1),transparent_50%)]" />

        <div className="relative p-8 md:p-10 lg:p-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            {/* Left content */}
            <div className="lg:col-span-7">
              <h2 className="text-3xl md:text-4xl font-light text-white leading-tight">
                Ready to experience<br/>
                <span className="text-[#C9A227]">Moonrise Valley?</span>
              </h2>
              
              <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed">
                Tell us your dates, group size, and the vibe you're after. 
                We'll respond with clear pricing and availability—no hidden costs, no surprises.
              </p>

              {/* CTA Buttons - Book Now + WhatsApp */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="flex-1 sm:flex-none rounded-xl bg-[#C9A227] px-8 py-4 text-center text-base font-semibold text-[#111] shadow-[0_10px_40px_rgba(201,162,39,0.25)] hover:bg-[#d4b03a] hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="contact-book-now"
                >
                  Enquire / Book
                </Link>
                
                {/* WhatsApp - Instant Booking Assistance */}
                <a
                  href={whatsappUrl(whatsappMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 text-base font-medium hover:bg-green-600/30 hover:border-green-500/50 transition-all duration-300"
                  data-testid="contact-whatsapp"
                >
                  <MessageCircle className="w-5 h-5" />
                  Instant Booking Assistance
                </a>
              </div>

              {/* Quick reassurance */}
              <p className="mt-4 text-xs text-white/50">
                Typically respond within 1 hour during business hours
              </p>
            </div>

            {/* Right - Contact details */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-white/10 bg-black/30 p-6">
                <div className="text-sm font-semibold text-white mb-4">Contact Us</div>
                
                <div className="space-y-4">
                  {/* Both Booking Phones */}
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-[#C9A227] mt-0.5" />
                    <div>
                      <div className="text-xs text-white/50 mb-1">Booking Helpline</div>
                      <a
                        href={`tel:${CONTACT.primaryPhone.replace(/-/g, "")}`}
                        className="block text-sm text-white hover:text-[#C9A227] transition font-medium"
                      >
                        {CONTACT.primaryPhone}
                      </a>
                      <a
                        href={`tel:${CONTACT.supportPhone.replace(/-/g, "")}`}
                        className="block text-sm text-white hover:text-[#C9A227] transition font-medium mt-1"
                      >
                        {CONTACT.supportPhone}
                      </a>
                    </div>
                  </div>

                  {/* Booking Email */}
                  <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-[#C9A227] mt-0.5" />
                    <div>
                      <div className="text-xs text-white/50 mb-1">For Bookings</div>
                      <a
                        href={`mailto:${CONTACT.emails.booking}`}
                        className="text-sm text-white/70 hover:text-white transition"
                      >
                        {CONTACT.emails.booking}
                      </a>
                    </div>
                  </div>

                  {/* Official & Business Emails */}
                  <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-white/40 mt-0.5" />
                    <div>
                      <div className="text-xs text-white/50 mb-1">Official & Business Inquiries</div>
                      <a
                        href={`mailto:${CONTACT.emails.operations}`}
                        className="block text-sm text-white/60 hover:text-white transition"
                      >
                        {CONTACT.emails.operations}
                      </a>
                      <a
                        href={`mailto:${CONTACT.emails.admin}`}
                        className="block text-sm text-white/60 hover:text-white transition mt-1"
                      >
                        {CONTACT.emails.admin}
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-3 pt-2 border-t border-white/10">
                    <MapPin className="w-4 h-4 text-[#C9A227] mt-0.5" />
                    <div className="text-sm text-white/60">
                      {CONTACT.address}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default ContactPanel;
